package models

/**
 * THE definition of "which rows count towards a total".
 *
 * One rule, one expression of it, for every language and every layer. It was
 * previously written out wherever it was needed — in the shared
 * [Transaction.excludeFromTotals] property, inline in each platform's
 * `MonthlyTotalsAggregator.TxnLike` adapter, and, in SQL, in the server's
 * `selectGoalAllocationsForUserInRange`. Copies of this rule have already cost
 * real money-facing bugs: Android's goal scan lost the exclusion clause and
 * reported May at 37.8% where web and iOS both read 26.0%.
 *
 * A SQL copy is the most dangerous kind, because no compiler or test relates it
 * to the Kotlin one — the two can drift silently for as long as nobody compares
 * a server number against a client number by hand. The server now selects the
 * flags and filters through this function instead of deciding in the WHERE
 * clause.
 *
 * ## The rule
 *
 * A **split parent** and a **distributed parent** are stand-ins for their
 * children, which carry the real amounts; counting the parent double-counts the
 * lot. An **explicitly excluded** row is the user saying "this is not part of my
 * finances". All three contribute to **nothing at all** — not income, not
 * expenses, and not goal allocations.
 *
 * Deliberately NOT part of this rule: `isTransfer`. A transfer is not income or
 * spending, but an assigned savings transfer IS the savings signal and its goal
 * allocation still counts. See [analytics.MonthlyTotalsAggregator]'s rule 2.
 */
object TransactionEligibility {

    /**
     * True when a row with these flags must be left out of every total.
     *
     * Takes the three flags rather than a transaction type so the server can
     * apply it to a raw SQLDelight row, iOS and Android to their own models,
     * and web to its dashboard row, without any of them restating it.
     */
    fun excludeFromTotals(
        isSplitParent: Boolean,
        isDistributedParent: Boolean,
        isExcluded: Boolean,
    ): Boolean = isSplitParent || isDistributedParent || isExcluded

    /** Convenience inverse — reads better at call sites that keep rows. */
    fun countsTowardTotals(
        isSplitParent: Boolean,
        isDistributedParent: Boolean,
        isExcluded: Boolean,
    ): Boolean = !excludeFromTotals(isSplitParent, isDistributedParent, isExcluded)
}
